/**
 * Realtime token provider for the public storefront.
 *
 * The user-scoped realtime connection token is brokered through the BFF
 * (`/api/realtime/token`) — exactly like every other public call — NOT a direct
 * browser→monolith Connect call (which the monolith's staff-only CORS blocked).
 * The browser then opens the WebSocket to the returned `websocketUrl` (a WS is not
 * subject to CORS). With this the public app makes ZERO direct monolith-API
 * calls — enforced by the `no_direct_monolith_client` lint.
 */
import { bff } from '@/shared/lib/bff';
import { clientLogger } from '@/shared/lib/client-logger';
import type { RealtimeTokens } from '@letsgethome/realtime';

export async function fetchRealtimeTokens(channel?: string): Promise<RealtimeTokens | null> {
  try {
    const result = await bff('/api/realtime/token', { channel });
    if (!result.success || !result.data?.connectionToken || !result.data.websocketUrl) {
      clientLogger.warn({ component: 'realtime' }, 'Missing tokens in realtime token response');
      return null;
    }
    return result.data;
  } catch (error) {
    clientLogger.warn(
      {
        component: 'realtime',
        error: error instanceof Error ? error.message : String(error),
      },
      'Failed to get realtime tokens'
    );
    return null;
  }
}
